import { logger } from "@/utils/logger";
/**
 * Parallel Data Loader
 * 
 * Manages non-blocking loading of inventory and showroom data.
 * Data loading is started immediately but not awaited during app init.
 * When AI response arrives and needs hydration, we await the promises.
 */

// Store loading promises globally so they can be awaited when needed
let inventoryLoadPromise: Promise<boolean> | null = null;
let showroomLoadPromise: Promise<boolean> | null = null;

/**
 * Start loading inventory data in background (non-blocking)
 * Returns immediately, stores promise for later awaiting
 */
export const startInventoryLoading = async (): Promise<void> => {
  if (inventoryLoadPromise) {
    logger.log('📦 Inventory loading already started');
    return;
  }
  
  try {
    const { loadInventoryIfEnabled } = await import('./inventoryUtils');
    logger.log('📦 Starting background inventory loading...');
    inventoryLoadPromise = loadInventoryIfEnabled()
      .then(() => {
        logger.log('📦 Background inventory loading complete');
        return true;
      })
      .catch((error) => {
        console.error('❌ Background inventory loading failed:', error);
        inventoryLoadPromise = null; // Allow retry on failure
        return false;
      });
  } catch (error) {
    console.error('❌ Failed to import inventoryUtils:', error);
  }
};

/**
 * Start loading showroom data in background (non-blocking)
 * Returns immediately, stores promise for later awaiting
 */
export const startShowroomLoading = async (): Promise<void> => {
  if (showroomLoadPromise) {
    logger.log('🏢 Showroom loading already started');
    return;
  }
  
  try {
    const { loadShowroomForSession } = await import('./showroomUtils');
    logger.log('🏢 Starting background showroom loading...');
    showroomLoadPromise = loadShowroomForSession().then((ok) => {
      logger.log('🏢 Background showroom loading complete', { ok });
      return ok;
    }).catch((error) => {
      console.error('❌ Background showroom loading failed:', error);
      showroomLoadPromise = null; // Allow retry on failure
      return false;
    });
  } catch (error) {
    console.error('❌ Failed to import showroomUtils:', error);
  }
};

/**
 * Start loading both inventory and showroom in parallel (non-blocking)
 * Call this during app init or when sending a message
 */
export const startParallelDataLoading = (): void => {
  // Fire-and-forget - start both without awaiting
  startInventoryLoading();
  startShowroomLoading();
};

/**
 * Ensure inventory data is loaded and ready for hydration
 * Call this before hydrating inventory vehicles
 */
export const ensureInventoryReady = async (): Promise<boolean> => {
  // Start loading if not already started
  if (!inventoryLoadPromise) {
    await startInventoryLoading();
  }
  
  if (inventoryLoadPromise) {
    return await inventoryLoadPromise;
  }
  
  return false;
};

/**
 * Ensure showroom data is loaded and ready for hydration
 * Call this before hydrating showroom vehicles
 */
export const ensureShowroomReady = async (): Promise<boolean> => {
  // Start loading if not already started
  if (!showroomLoadPromise) {
    await startShowroomLoading();
  }
  
  if (showroomLoadPromise) {
    return await showroomLoadPromise;
  }
  
  return false;
};

/**
 * Ensure both inventory and showroom are ready
 * Call this when AI response arrives and needs hydration
 */
export const ensureDataReadyForHydration = async (): Promise<{
  inventoryReady: boolean;
  showroomReady: boolean;
}> => {
  const [inventoryReady, showroomReady] = await Promise.all([
    ensureInventoryReady(),
    ensureShowroomReady()
  ]);
  
  logger.log('🔄 Data ready for hydration:', { inventoryReady, showroomReady });
  
  return { inventoryReady, showroomReady };
};

/**
 * Check if data loading has been started (for debugging)
 */
export const getLoadingStatus = (): {
  inventoryStarted: boolean;
  showroomStarted: boolean;
} => {
  return {
    inventoryStarted: inventoryLoadPromise !== null,
    showroomStarted: showroomLoadPromise !== null
  };
};

/**
 * Reset loading state (for testing or session reset)
 */
export const resetLoadingState = (): void => {
  inventoryLoadPromise = null;
  showroomLoadPromise = null;
};
